import React, { useState, useEffect } from "react";
import { useMutation, gql } from "@apollo/client";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Slider,
  TextField,
  Typography,
} from "@mui/material";
import { Save as SaveIcon } from "@mui/icons-material";

const UPDATE_BOT_AI_CONFIG = gql`
  mutation UpdateBotAIConfig(
    $widgetId: ID!
    $aiConfig: AIConfigInput!
    $ragConfig: RAGConfigInput
  ) {
    updateBotAIConfig(
      widgetId: $widgetId
      aiConfig: $aiConfig
      ragConfig: $ragConfig
    ) {
      aiConfig {
        provider
        model
        temperature
        maxTokens
      }
      ragConfig {
        topK
        minRelevanceScore
        includeSourceCitations
      }
    }
  }
`;

const AI_MODELS = {
  openai: [
    { value: "gpt-4o-mini", label: "GPT-4o Mini (Recommended)" },
    { value: "gpt-4o", label: "GPT-4o" },
    { value: "gpt-4-turbo", label: "GPT-4 Turbo" },
    { value: "gpt-3.5-turbo", label: "GPT-3.5 Turbo" },
  ],
  anthropic: [
    { value: "claude-3-haiku-20240307", label: "Claude 3 Haiku (Fast)" },
    { value: "claude-3-sonnet-20240229", label: "Claude 3 Sonnet" },
    { value: "claude-3-opus-20240229", label: "Claude 3 Opus" },
  ],
};

function AIConfigPanel({
  widgetId,
  botSettings,
  loading,
  onRefetch,
  showAlert,
}) {
  const [formData, setFormData] = useState({
    provider: "openai",
    model: "gpt-4o-mini",
    temperature: 0.7,
    maxTokens: 500,
    topK: 5,
    minRelevanceScore: 0.7,
    includeSourceCitations: true,
  });
  const [hasChanges, setHasChanges] = useState(false);

  const [updateConfig, { loading: saving }] = useMutation(
    UPDATE_BOT_AI_CONFIG,
    {
      onCompleted: () => {
        showAlert("AI configuration saved successfully", "success");
        setHasChanges(false);
        onRefetch?.();
      },
      onError: (error) => {
        showAlert(`Error saving configuration: ${error.message}`, "error");
      },
    }
  );

  // Initialize form data from props
  useEffect(() => {
    if (botSettings) {
      setFormData({
        provider: botSettings.aiConfig?.provider ?? "openai",
        model: botSettings.aiConfig?.model ?? "gpt-4o-mini",
        temperature: botSettings.aiConfig?.temperature ?? 0.7,
        maxTokens: botSettings.aiConfig?.maxTokens ?? 500,
        topK: botSettings.ragConfig?.topK ?? 5,
        minRelevanceScore: botSettings.ragConfig?.minRelevanceScore ?? 0.7,
        includeSourceCitations:
          botSettings.ragConfig?.includeSourceCitations ?? true,
      });
    }
  }, [botSettings]);

  const handleChange = (field) => (event) => {
    const value =
      event.target.type === "checkbox"
        ? event.target.checked
        : event.target.value;
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    setHasChanges(true);
  };

  const handleSliderChange = (field) => (event, newValue) => {
    setFormData((prev) => ({
      ...prev,
      [field]: newValue,
    }));
    setHasChanges(true);
  };

  const handleProviderChange = (event) => {
    const provider = event.target.value;
    const defaultModel = AI_MODELS[provider]?.[0]?.value || "gpt-4o-mini";
    setFormData((prev) => ({
      ...prev,
      provider,
      model: defaultModel,
    }));
    setHasChanges(true);
  };

  const handleSave = () => {
    updateConfig({
      variables: {
        widgetId,
        aiConfig: {
          provider: formData.provider,
          model: formData.model,
          temperature: formData.temperature,
          maxTokens: formData.maxTokens,
        },
        ragConfig: {
          topK: formData.topK,
          minRelevanceScore: formData.minRelevanceScore,
          includeSourceCitations: formData.includeSourceCitations,
        },
      },
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        AI Configuration
      </Typography>
      <Typography variant="body2" color="textSecondary" paragraph>
        Configure the AI model and response generation settings.
      </Typography>

      <Grid container spacing={3}>
        {/* AI Provider & Model */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                AI Provider & Model
              </Typography>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Provider</InputLabel>
                <Select
                  value={formData.provider}
                  label="Provider"
                  onChange={handleProviderChange}
                >
                  <MenuItem value="openai">OpenAI</MenuItem>
                  <MenuItem value="anthropic">Anthropic</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Model</InputLabel>
                <Select
                  value={formData.model}
                  label="Model"
                  onChange={handleChange("model")}
                >
                  {AI_MODELS[formData.provider]?.map((model) => (
                    <MenuItem key={model.value} value={model.value}>
                      {model.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </CardContent>
          </Card>
        </Grid>

        {/* Response Parameters */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Response Parameters
              </Typography>

              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" gutterBottom>
                  Temperature: {formData.temperature}
                </Typography>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  display="block"
                  gutterBottom
                >
                  Lower = more focused, Higher = more creative
                </Typography>
                <Slider
                  value={formData.temperature}
                  onChange={handleSliderChange("temperature")}
                  step={0.1}
                  min={0}
                  max={1}
                  marks={[
                    { value: 0, label: "0" },
                    { value: 0.5, label: "0.5" },
                    { value: 1, label: "1" },
                  ]}
                />
              </Box>

              <TextField
                fullWidth
                label="Max Tokens"
                type="number"
                value={formData.maxTokens}
                onChange={handleChange("maxTokens")}
                inputProps={{ min: 100, max: 4000 }}
                helperText="Maximum length of the bot's response (100-4000)"
              />
            </CardContent>
          </Card>
        </Grid>

        {/* RAG Configuration */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                RAG (Retrieval) Configuration
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                Configure how the bot retrieves and uses knowledge base content
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Top K Results"
                    type="number"
                    value={formData.topK}
                    onChange={handleChange("topK")}
                    inputProps={{ min: 1, max: 20 }}
                    helperText="Number of relevant chunks to retrieve"
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <Box>
                    <Typography variant="body2" gutterBottom>
                      Min Relevance Score: {formData.minRelevanceScore}
                    </Typography>
                    <Slider
                      value={formData.minRelevanceScore}
                      onChange={handleSliderChange("minRelevanceScore")}
                      step={0.05}
                      min={0.5}
                      max={0.95}
                      marks={[
                        { value: 0.5, label: "0.5" },
                        { value: 0.7, label: "0.7" },
                        { value: 0.95, label: "0.95" },
                      ]}
                    />
                    <Typography variant="caption" color="textSecondary">
                      Minimum similarity score for relevant content
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Include Source Citations</InputLabel>
                    <Select
                      value={formData.includeSourceCitations}
                      label="Include Source Citations"
                      onChange={handleChange("includeSourceCitations")}
                    >
                      <MenuItem value={true}>
                        Yes - Show sources in responses
                      </MenuItem>
                      <MenuItem value={false}>No - Hide sources</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Save Button */}
        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} />
          <Box display="flex" justifyContent="flex-end">
            <Button
              variant="contained"
              color="primary"
              startIcon={
                saving ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <SaveIcon />
                )
              }
              onClick={handleSave}
              disabled={saving || !hasChanges}
            >
              {saving ? "Saving..." : "Save Configuration"}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}

export default AIConfigPanel;
